import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ErrorMessage, Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import EditorInfoIcon from '@atlaskit/icon/core/migration/information--editor-info';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import { P300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Flex } from '@atlassian/eoc-flex';

import commonMessages from '../../../messages';
import { type RuleType } from '../../../types';
import { EditableRule } from '../../editable-rule';
import { RuleInput } from '../../rule-input';
import { FieldResetter } from '../../styled';

import messages from './messages';
import { StyledButton, WarningWrapper } from './styled';
import { type EscalationRulesInputsProps } from './types';

export const EscalationRulesInputs = ({
	notifyTypes,
	rules,
	onDelete,
	onChange,
	onProcess,
}: EscalationRulesInputsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isAdding, setIsAdding] = useState(false);
	const [editingIndex, setEditingIndex] = useState<number>();

	useEffect(() => {
		if (onProcess) {
			if (isAdding || editingIndex) {
				onProcess(true);
				return;
			}

			onProcess(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAdding, editingIndex]);

	const isProcessing = isAdding || editingIndex !== undefined;

	return (
		<Flex flexDirection="column" rowGap={1}>
			<Flex alignItems="center" columnGap={0.5}>
				<Heading size="small">{formatMessage(messages.escalationRules)}</Heading>
				{(!rules || rules?.length === 0) && (
					<Tooltip content={formatMessage(messages.escalationRulesTooltip)} position="bottom-start">
						<InfoIcon LEGACY_size="small" color={token('color.icon.subtle', '#626F86')} label="" />
					</Tooltip>
				)}
			</Flex>
			<div>
				<FieldResetter>
					<Field<RuleType[]> name="rules" defaultValue={rules || []}>
						{({ fieldProps, error }) => {
							const rulesCount = fieldProps.value.length;

							if (rulesCount === 50) {
								const event = createAnalyticsEvent({});
								fireUIAnalytics(event, 'escalationPolicyRuleLimit reached');
							}

							return (
								<Flex flexDirection="column">
									{fieldProps.value.map((rule, i) => (
										<EditableRule
											key={i}
											isEditing={editingIndex === i}
											shouldHideEditButton={isAdding}
											shouldHideDeleteButton={isAdding || editingIndex !== undefined}
											onEdit={() => setEditingIndex(i)}
											rule={rule}
											notifyTypes={notifyTypes}
											onCancel={() => setEditingIndex(undefined)}
											onDelete={(value) => {
												const copiedValues = [...fieldProps.value];
												const newValues = copiedValues.filter(
													(_, deletingIndex) => i !== deletingIndex,
												);
												fieldProps.onChange(newValues);
												if (onDelete) {
													onDelete(value);
												}
											}}
											onChange={(value) => {
												const copiedValues = [...fieldProps.value];
												copiedValues[i] = value;
												fieldProps.onChange(copiedValues);
												setEditingIndex(undefined);

												if (onChange) {
													onChange(rule);
												}
											}}
										/>
									))}
									{isAdding && (
										<RuleInput
											onChange={(value) => {
												const copiedValues = [...fieldProps.value];
												copiedValues.push(value);
												fieldProps.onChange(copiedValues);
												setIsAdding(false);
											}}
											onCancel={() => setIsAdding(false)}
											notifyTypes={notifyTypes}
											warningMessage={
												rulesCount === 49 && (
													<>
														<EditorInfoIcon
															spacing="spacious"
															color={token('color.icon.discovery', P300)}
															label=""
														/>
														{formatMessage(messages.lastRule)}
													</>
												)
											}
										/>
									)}
									{rulesCount === 50 ? (
										<WarningWrapper>
											<EditorInfoIcon
												spacing="spacious"
												color={token('color.icon.discovery', P300)}
												label=""
											/>
											{formatMessage(messages.ruleLimit)}
										</WarningWrapper>
									) : (
										!isAdding &&
										editingIndex === undefined && (
											<div>
												<StyledButton
													spacing="none"
													iconBefore={
														<EditorAddIcon color="currentColor" spacing="spacious" label="" />
													}
													appearance="link"
													onClick={() => {
														setIsAdding(true);
														fieldProps.onBlur();
													}}
												>
													{formatMessage(messages.addRule)}
												</StyledButton>
											</div>
										)
									)}
									{error === 'required' && !isProcessing && (
										<ErrorMessage>{formatMessage(commonMessages.fieldRequired)}</ErrorMessage>
									)}
									{error === 'is-processing' && isProcessing && (
										<ErrorMessage>{formatMessage(messages.isProcessing)}</ErrorMessage>
									)}
								</Flex>
							);
						}}
					</Field>
				</FieldResetter>
			</div>
		</Flex>
	);
};
